import { requestData, requestPage, type Page, type PageQuery } from '@/lib/api-client';
import type { Medicine, Uuid } from './types';

export type MedicineSearchQuery = PageQuery & {
  /** Also show this pharmacy's own drafts. The server checks the caller staffs it. */
  draftsOf?: Uuid;
  /** The whole catalogue, this pharmacy's stock first, each hit with its shelf quantity and MRP. */
  preferStockOf?: Uuid;
  q?: string;
  /** Sent as the literal strings "true"/"false" — the backend parses it that way. */
  prescriptionRequired?: boolean;
  /**
   * Narrows the search to what ONE pharmacy can dispense right now — listed,
   * active, available and with stock on hand.
   *
   * Sent only by the prescription reader, where the question is "what can I
   * give this person" rather than "what exists". The stock and purchase
   * screens deliberately leave it off: listing or receiving a product means
   * finding it in the catalog before you hold any of it.
   *
   * The backend checks the caller actually staffs the pharmacy named here.
   */
  stockedBy?: Uuid;
};

/**
 * The catalog is central and read-only from here: a pharmacy stocks and prices
 * a medicine, it does not get to define one. That is what keeps one product one
 * row across every branch on the platform.
 *
 * Search is the backend's four-tier match — exact code, then alias, then
 * weighted full text, then trigram fuzzy — so a counter typing "crocine" under
 * time pressure still lands on the right row.
 */
export function searchMedicines(query: MedicineSearchQuery): Promise<Page<Medicine>> {
  const { prescriptionRequired, ...rest } = query;
  return requestPage<Medicine>('/medicines', {
    query: {
      ...rest,
      prescriptionRequired:
        prescriptionRequired === undefined ? undefined : String(prescriptionRequired),
    },
  });
}

export function getMedicine(medicineId: Uuid): Promise<Medicine> {
  return requestData<Medicine>(`/medicines/${medicineId}`);
}

/** What `POST /medicines/resolve` gives back for one printed identifier. */
export type ResolvedCode = {
  code: string;
  medicine:
    | (Pick<Medicine, 'id' | 'sku' | 'name' | 'packSize' | 'barcode' | 'gstRate' | 'typicalMrp'> & {
        packForm?: string | null;
        baseUnit?: string | null;
        unitsPerPack?: number | null;
        packsPerBox?: number | null;
      })
    | null;
};

/**
 * Resolves many printed identifiers at once — for importing a supplier's file,
 * where looking each row up on its own would be five hundred round trips.
 *
 * Exact SKU or barcode only. Anything else comes back null and has to be
 * matched by a human: an almost-right name is how the wrong strength gets
 * booked in, and nobody re-reads an import.
 */
export function resolveCodes(codes: string[]): Promise<ResolvedCode[]> {
  return requestData<ResolvedCode[]>('/medicines/resolve', { method: 'POST', body: { codes } });
}

import type { Brand, DraftMedicineInput, Manufacturer } from './types';

/** The register of makers. Small enough to fetch once and filter in the picker. */
export function listManufacturers(query: { limit?: number } = {}): Promise<Page<Manufacturer>> {
  return requestPage<Manufacturer>('/manufacturers', { query });
}

export function listBrands(
  query: { q?: string; manufacturerId?: Uuid; limit?: number } = {},
): Promise<Page<Brand>> {
  return requestPage<Brand>('/brands', { query });
}

/**
 * A product the catalogue does not have, keyed from the pack in hand.
 *
 * It comes back usable at once - as a DRAFT that only this pharmacy can see -
 * and the platform publishes it for everyone once it has been checked.
 */
export function createDraftMedicine(pharmacyId: Uuid, body: DraftMedicineInput): Promise<Medicine> {
  return requestData<Medicine>(`/pharmacies/${pharmacyId}/catalog/drafts`, {
    method: 'POST',
    body: body,
  });
}
